import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import {
  asyncSendEdiOrder,
  asyncSendFranchiseOrder,
  deleteEdiUploadAsync,
  deleteFranchiseUploadAsync,
  getEdiList,
  getEdiVersionList,
  uploadEdiFile,
  uploadFranchiseFile
} from 'services/batchOrder'
import {
  EdiDeleteParams,
  EdiFormValues,
  EdiSendBody,
  EdiSendResponse,
  EdiUploadError,
  EdiVersion,
  FranchiseFormValues,
  FranchiseUploadError,
  IntlEDI
} from 'services/batchOrder/interfaces'
import { AppThunk } from 'store/store'
import { formatDate } from 'commons/utils/date'

interface UploadSuccess {
  boxVol: number
  pairVol: number
  totalVal: number
}

interface UploadData {
  processId: string
  hasOrders: boolean
  ediErrors: EdiUploadError[]
  franchiseErrors: FranchiseUploadError[]
  errorDownloadLink: string
  uploadSuccess: UploadSuccess
}

type InitialStateType = {
  loadingEdiOptions: boolean
  ediOptions: IntlEDI[]
  loadingVersionOptions: boolean
  versionOptions: EdiVersion[]
  loadingUpload: boolean
  uploadData: UploadData
  loadingDeletion: boolean
  loadingSend: boolean
  generatedEdiOrders: EdiSendResponse
  hasConfirmedFranchiseOrders: boolean
}

const initialState: InitialStateType = {
  loadingEdiOptions: true,
  ediOptions: [],
  loadingVersionOptions: false,
  versionOptions: [],
  loadingUpload: false,
  loadingDeletion: false,
  loadingSend: false,
  generatedEdiOrders: {} as EdiSendResponse,
  hasConfirmedFranchiseOrders: false,
  uploadData: {
    processId: '',
    hasOrders: false,
    ediErrors: [],
    franchiseErrors: [],
    errorDownloadLink: '',
    uploadSuccess: {
      boxVol: 0,
      pairVol: 0,
      totalVal: 0
    }
  }
}

const batchOrder = createSlice({
  name: 'batchOrder',
  initialState,
  reducers: {
    setLoadingEdiOptions(state, action: PayloadAction<boolean>) {
      state.loadingEdiOptions = action.payload
    },
    setEdiOptions(state, action: PayloadAction<IntlEDI[]>) {
      state.ediOptions = action.payload
    },
    setLoadingVersionOptions(state, action: PayloadAction<boolean>) {
      state.loadingVersionOptions = action.payload
    },
    setVersionOptions(state, action: PayloadAction<EdiVersion[]>) {
      state.versionOptions = action.payload
    },
    setLoadingUpload(state, action: PayloadAction<boolean>) {
      state.loadingUpload = action.payload
    },
    setUploadData(state, action: PayloadAction<UploadData>) {
      state.uploadData = action.payload
    },
    setLoadingDeletion(state, action: PayloadAction<boolean>) {
      state.loadingDeletion = action.payload
    },
    setLoadingSend(state, action: PayloadAction<boolean>) {
      state.loadingSend = action.payload
    },
    setGeneratedEdiOrders(state, action: PayloadAction<EdiSendResponse>) {
      state.generatedEdiOrders = action.payload
    },
    setConfirmedFranchiseOrders(state, action: PayloadAction<boolean>) {
      state.hasConfirmedFranchiseOrders = action.payload
    },
    clearUploadData(state) {
      state.uploadData = {
        processId: '',
        ediErrors: [],
        franchiseErrors: [],
        errorDownloadLink: '',
        hasOrders: false,
        uploadSuccess: {
          boxVol: 0,
          pairVol: 0,
          totalVal: 0
        }
      }
    },
    clearGeneratedData(state) {
      state.generatedEdiOrders = {} as EdiSendResponse
      state.hasConfirmedFranchiseOrders = false
    }
  }
})

export const {
  setLoadingEdiOptions,
  setEdiOptions,
  setLoadingVersionOptions,
  setVersionOptions,
  setLoadingUpload,
  setUploadData,
  setLoadingDeletion,
  setLoadingSend,
  setGeneratedEdiOrders,
  setConfirmedFranchiseOrders,
  clearUploadData,
  clearGeneratedData
} = batchOrder.actions

export const batchOrderContext = batchOrder.reducer

export const getEdiOptions = (): AppThunk => async (dispatch) => {
  dispatch(setLoadingEdiOptions(true))
  try {
    const data = await getEdiList()
    if (data) {
      dispatch(setEdiOptions(data))
    }
  } catch (error) {
    toast.error('Erro ao carregar opções de EDI.')
  } finally {
    dispatch(setLoadingEdiOptions(false))
  }
}

export const getEdiVersionOptions =
  (idEDI: string): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingVersionOptions(true))
    try {
      const data = await getEdiVersionList(idEDI)
      if (data) {
        dispatch(setVersionOptions(data))
      }
    } catch (error) {
      toast.error('Erro ao carregar versões de EDI.')
    } finally {
      dispatch(setLoadingVersionOptions(false))
    }
  }

export const sendEdiUpload =
  (formFields: EdiFormValues, setProgress?: (progress: number) => void): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingUpload(true))
    if (formFields.edi && formFields.uploadedFile) {
      const form = new FormData()

      const params = {
        cd_edi: formFields.edi.cd_edi,
        nm_arqv_edi: formFields.uploadedFile.name,
        ds_text_orde_vend: formFields.headerText,
        ds_text_orde_vend_item: formFields.itemText,
        fl_prmt_qtde_mult: 'N',
        cd_edi_vers: formFields.version?.cd_edi_vers ?? '',
        dt_prev_fatm: formatDate(formFields.expectedDate, 'YYYY-MM-DD'),
        cd_mes_pedi: formatDate(formFields.orderMonth, 'YYYYMM'),
        cd_mes_fatm: formatDate(formFields.billingMonth, 'YYYYMM'),
        ds_armz: formFields.warehouse
      }

      form.append('parms', JSON.stringify(params))
      form.append('arquivo', formFields.uploadedFile)

      uploadEdiFile(form, setProgress)
        .then((data) => {
          dispatch(
            setUploadData({
              processId: data.id_prcs_edi,
              hasOrders: true,
              ediErrors: [],
              franchiseErrors: [],
              errorDownloadLink: '',
              uploadSuccess: {
                boxVol: data.volume_caixas ?? 0,
                pairVol: data.volume_pares ?? 0,
                totalVal: data.valor_total ?? 0
              }
            })
          )
          toast.success('Planilha carregada com sucesso!')
        })
        .catch((error) => {
          if (error.response) {
            const data = error.response.data
            dispatch(
              setUploadData({
                processId: data.id_prcs_edi,
                ediErrors: data.criticas,
                franchiseErrors: [],
                errorDownloadLink: '',
                uploadSuccess: { boxVol: 0, pairVol: 0, totalVal: 0 },
                hasOrders: false
              })
            )
            toast.success('Planilha carregada com sucesso!')
          }
        })
        .finally(() => dispatch(setLoadingUpload(false)))
    }
  }

export const deleteEdiUpload =
  (payload: EdiDeleteParams): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingDeletion(true))

    try {
      const response = await deleteEdiUploadAsync(payload)
      if (response.return_code === 0) {
        dispatch(clearUploadData())
        toast.success('Pedidos excluídos com sucesso!')
      }
    } catch {
      toast.error('Erro ao excluir processamento EDI')
    } finally {
      dispatch(setLoadingDeletion(false))
    }
  }

export const SendEdiOrders =
  (payload: EdiSendBody): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingSend(true))

    try {
      const response = await asyncSendEdiOrder(payload)
      if (response.return_code === 0) {
        dispatch(setGeneratedEdiOrders(response))
        dispatch(clearUploadData())
      }
    } catch {
      toast.error('Erro ao enviar pedidos EDI')
    } finally {
      dispatch(setLoadingSend(false))
    }
  }

export const sendFranchiseUpload =
  (formFields: FranchiseFormValues, setProgress?: (progress: number) => void): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingUpload(true))
    if (formFields.file) {
      const form = new FormData()

      form.append('cd_empr', formFields.company)
      form.append('cd_orgn_vend', formFields.organization)
      form.append('cd_canl_dstb', formFields.market)
      form.append('cd_seto_atvd', formFields.sector)
      form.append('cd_escr_vend', formFields.office)
      form.append('cd_equi_vend', formFields.region)
      form.append('cd_regi_vend', formFields.zone)
      form.append('dt_ano_mes_cota', formFields.quotaMonth)
      form.append('nr_linh_dado', formFields.initialDataLine)
      form.append('nr_coln_tamh_inil', formFields.initialSizeCol)
      form.append('nr_coln_tamh_finl', formFields.finalSizeCol)
      form.append('nr_coln_clie_inil', formFields.initialClientCol)
      form.append('nr_coln_clie_finl', formFields.finalClientCol)
      form.append('file', formFields.file)

      uploadFranchiseFile(form, setProgress)
        .then((data) => {
          dispatch(
            setUploadData({
              processId: formFields.file?.name ?? '',
              hasOrders: true,
              ediErrors: [],
              franchiseErrors: data.ItensInvalidosResponse ?? [],
              errorDownloadLink: data.LinkDownloadArquivoCritica ?? '',
              uploadSuccess: {
                boxVol: data.TotalCaixa ?? 0,
                pairVol: data.TotalPares ?? 0,
                totalVal: data.ValorTotal ?? 0
              }
            })
          )
          toast.success('Planilha carregada com sucesso!')
        })
        .catch((error) => {
          if (error.response) {
            const data = error.response.data

            if (error.response.status === 500) {
              toast.error(
                error.response.data.return_chav ??
                  'Não foi possível carregar a planilha devido a uma instabilidade no sistema. Tente novamente mais tarde.'
              )
            }
            if (data.MensagemRetorno?.includes('Arquivo já importado')) {
              toast.error(
                'Não foi possível carregar a planilha porque já existe um arquivo com esse nome em nosso sistema. Altere o nome do arquivo e tente enviar novamente.'
              )
            }
            if (data.ItensInvalidosResponse?.length) {
              dispatch(
                setUploadData({
                  processId: formFields.file?.name ?? '',
                  ediErrors: [],
                  franchiseErrors: data.ItensInvalidosResponse,
                  errorDownloadLink: data.LinkDownloadArquivoCritica ?? '',
                  uploadSuccess: { boxVol: 0, pairVol: 0, totalVal: 0 },
                  hasOrders: false
                })
              )
              toast.success('Planilha carregada com sucesso!')
            }
          }
        })
        .finally(() => dispatch(setLoadingUpload(false)))
    }
  }

export const SendFranchiseOrders =
  (filename: string): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingSend(true))

    try {
      const response = await asyncSendFranchiseOrder(filename.toLocaleUpperCase())
      if (response.status === 202) {
        dispatch(setConfirmedFranchiseOrders(true))
        dispatch(clearUploadData())
      }
    } catch {
      toast.error('Erro ao enviar pedidos.')
    } finally {
      dispatch(setLoadingSend(false))
    }
  }

export const deleteFranchiseUpload =
  (filename: string): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingDeletion(true))

    try {
      const response = await deleteFranchiseUploadAsync(filename.toLocaleUpperCase())
      if (response.IsCompletedSuccessfully) {
        dispatch(clearUploadData())
        toast.success('Pedidos excluídos com sucesso!')
      }
    } catch {
      toast.error('Erro ao excluir processamento.')
    } finally {
      dispatch(setLoadingDeletion(false))
    }
  }
